import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import {
  ApiRequestType,
  ErrorCode,
  ErrorDetails,
  Gender,
  Guid,
  Money,
  PatchRequestBody,
  Quote,
  QuotePaymentStatus,
} from "../Common/Types";
import { QuoteData, QuoteModificationResponse, useQuoteContext } from "./QuoteContext/QuoteContext";
import { fetchRetry } from "../Common/services/FetchRetry";
import { AppConfig } from "../AppConfig";
import { GenerateHeaderInformation } from "../Common/Helpers/ApiHelper";
import { useSessionContext } from "./SessionContext/SessionContext";
import { useImmer } from "use-immer";
import { convertToErrorDetails, returnJsonOrThrowError, logError } from "../Common/services/ErrorService";
import { processBundleItemsAndPaymentStatus } from "../Common/services/QuoteService";
import { JWT_API_KEY } from "../Common/Constants";
import { QuotationApi } from "../apis/quotationApi";
import { ProductCatalogApi } from "../apis/productCatalogApi";
import { SalesItemResponseDto } from "../apis/generatedProductCatalogApi";
import { InstallmentsResponseDto } from "../apis/generatedQuotationApi";

export type ReservationPriceResponse = Money & {
  quoteId: Guid;
};

export type QuoteDraft = {
  quoteDraft: QuoteData;
  isQuoteDraftEdited: boolean;
};

interface QuotesDetailsContextValue {
  updateQuote: (x: Quote, body: PatchRequestBody[]) => Promise<QuoteModificationResponse>;
  saveQuote: (x: Quote) => Promise<QuoteModificationResponse>;
  fetchInstallments: () => Promise<void>;
  payAllDiscount: () => number;
  quoteDraft?: QuoteDraft;
  loadingQuoteError?: ErrorDetails;
  loadingQuote: boolean;
  reservationPrice?: Money;
  loadingInstallments: boolean;
  paidFullDiscount?: SalesItemResponseDto | null;
  installments?: InstallmentsResponseDto | null;
}

const initialValue: QuotesDetailsContextValue = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  updateQuote: (_) => Promise.resolve(null as unknown as QuoteModificationResponse),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  saveQuote: (_) => Promise.resolve(null as unknown as QuoteModificationResponse),
  fetchInstallments: () => Promise.resolve(),
  payAllDiscount: () => 0,
  quoteDraft: undefined,
  loadingQuoteError: undefined,
  loadingQuote: true,
  reservationPrice: undefined,
  loadingInstallments: false,
};

const QuoteDetailsContext = createContext<QuotesDetailsContextValue>(initialValue);

interface QuoteContextProviderProps {
  children: ReactNode;
  fetchDraft: boolean;
  fetchInstallments?: boolean;
}

function getErrorDetails(errorDetails: ErrorDetails): QuoteModificationResponse {
  return {
    isSuccessful: false,
    error: errorDetails,
  };
}

function getQuoteWithReservationPrice(
  rawQuote: Quote,
  serverQuote: Quote,
  resPrice: ReservationPriceResponse,
  isEdited: boolean
) {
  const newDraft: QuoteData = {
    rawQuoteData: serverQuote,
    quoteData: {
      ...rawQuote,
      status: QuotePaymentStatus.Unreserved,
    },
    reservationPrice: resPrice,
  };
  return {
    quoteDraft: newDraft,
    isQuoteDraftEdited: isEdited,
  };
}

const QuoteDetailsContextProvider: React.FC<QuoteContextProviderProps> = ({ children, fetchDraft = true }) => {
  const { session } = useSessionContext();
  const { addedQuote, selected } = useQuoteContext();
  const [quoteDraft, updateQuoteDraft] = useImmer<QuoteDraft | undefined>(undefined);
  const [loadingQuoteDetails, setLoadingQuoteDetails] = useState<boolean>(true);
  const [loadingInstallments, setLoadingInstallments] = useState<boolean>(false);
  const [loadingError, setLoadingError] = useState<ErrorDetails | undefined>(undefined);
  const [currentQuoteId, setCurrentQuoteId] = useState<Guid | undefined>(undefined);
  const [resPrice, setResPrice] = useState<ReservationPriceResponse | undefined>(undefined);
  const [paidFullDiscount, setPaidFullDiscount] = useState<SalesItemResponseDto | undefined | null>(undefined);
  const [installments, setInstallments] = useState<InstallmentsResponseDto | undefined | null>(undefined);
  const quotationApi = new QuotationApi();
  const productCatalogApi = new ProductCatalogApi();

  useEffect(() => {
    // get reservation price for selected quote
    if (selected && !resPrice) {
      setCurrentQuoteId(selected.quoteData.id);
      setLoadingQuoteDetails(true);
      // note: after saving new quote in TB, quote is send sync to the SF, right afterwards the reservation price call is made
      // which might return 403 if the quote from SF didn't propagate to starlink yet using kafka; that's the reason why
      // retry is enabled here for 403 code exceptionally
      fetchRetry(
        `${AppConfig.api.quotation}/api/v3/quote/reservationprice/${selected.quoteData.id}`,
        {
          method: ApiRequestType.GET,
          headers: GenerateHeaderInformation(session.jwtAccessToken, JWT_API_KEY),
        },
        [403]
      )
        .then((response) => returnJsonOrThrowError<ReservationPriceResponse>(response))
        .then((reservationPriceResponse) => {
          setResPrice(reservationPriceResponse);
          if (selected && reservationPriceResponse && !quoteDraft && fetchDraft) {
            fetchRetry(`${AppConfig.api.quotation}/quotes/${selected.quoteData.id}/draft`, {
              method: ApiRequestType.GET,
              headers: GenerateHeaderInformation(session.jwtAccessToken, JWT_API_KEY),
            })
              .then((response) => returnJsonOrThrowError<Quote>(response))
              .then((result) => {
                const processedDraft = processBundleItemsAndPaymentStatus(result, false);
                updateQuoteDraft(getQuoteWithReservationPrice(processedDraft, result, reservationPriceResponse, false));
              })
              .catch((error) => {
                const errorDetails = convertToErrorDetails(error, ErrorCode.QuoteDraftLoadingFailed, {
                  fallbackMessage: `Could not load quote.`,
                  additionalDetails: `Tracking id: ${session.opportunity.id}. Quote id: ${selected.quoteData.id}`,
                });
                logError(errorDetails);
                setLoadingError(errorDetails);
              })
              .finally(() => {
                setLoadingQuoteDetails(false);
              });
          }
        })
        .catch((error) => {
          const errorDetails = convertToErrorDetails(error, ErrorCode.ReservationPriceLoadingFailed, {
            fallbackMessage: `Could not load reservation price for quote ${selected?.quoteData.id}.`,
            additionalDetails: `Tracking id: ${session.opportunity.id}`,
          });
          logError(errorDetails);
          setLoadingError(errorDetails);
        })
        .finally(() => {
          if (!fetchDraft) {
            setLoadingQuoteDetails(false);
          }
        });
    }
    if (currentQuoteId && selected && selected.quoteData.id !== currentQuoteId) {
      setResPrice(undefined);
      setInstallments(undefined);
      setPaidFullDiscount(undefined);
      updateQuoteDraft(undefined);
    }
  }, [selected?.quoteData.id, resPrice]);

  const payAllDiscount = () => {
    if (
      installments?.payInFullDiscount === true &&
      paidFullDiscount?.price?.prices &&
      paidFullDiscount.price.prices.length > 0 &&
      paidFullDiscount.price.prices[0].value &&
      paidFullDiscount.price.prices[0].value > 0
    ) {
      return paidFullDiscount.price.prices[0].value;
    }
    return 0;
  };

  const fetchInstallments = async () => {
    if ((installments !== undefined && paidFullDiscount !== undefined) || loadingInstallments || selected == null) {
      return;
    }
    setLoadingInstallments(true);

    const installmentsPromise = quotationApi.getInstallments(selected.rawQuoteData.id);
    const payInFullDiscountPromise = productCatalogApi.getPayInFullDiscount(
      selected.rawQuoteData.market,
      selected.rawQuoteData.program,
      selected.rawQuoteData.prices.totalPrice.currency,
      selected.rawQuoteData.prices.brochure,
      selected.rawQuoteData.prices.season
    );

    Promise.allSettled([installmentsPromise, payInFullDiscountPromise])
      .then(([installmentsResult, discountResult]) => {
        const allowFullPaymentDiscount = true;
        setInstallments(installmentsResult.status === "fulfilled" ? installmentsResult.value : null);
        setPaidFullDiscount(
          discountResult.status === "fulfilled" && allowFullPaymentDiscount ? discountResult.value : null
        );
      })
      .finally(() => {
        setLoadingInstallments(false);
      });
  };
  const upgrade = async (draft: Quote, body: PatchRequestBody[]): Promise<QuoteModificationResponse> => {
    if (!draft.id) {
      return getErrorDetails({
        code: ErrorCode.QuoteUpgradeFailed,
        details: {
          additionalDetails: `Cannot upgrade quote draft - draft id not provided.`,
        },
      });
    }

    return await fetchRetry(`${AppConfig.api.quotation}/quote-drafts/${draft.id}`, {
      method: ApiRequestType.PATCH,
      headers: GenerateHeaderInformation(session.jwtAccessToken, JWT_API_KEY),
      body: JSON.stringify(body),
    })
      .then((response) => returnJsonOrThrowError<Quote>(response))
      .then((result) => {
        updateQuoteDraft(getQuoteWithReservationPrice(result, result, resPrice!, true));
        return { quote: result as Quote, isSuccessful: true };
      })
      .catch((error) => {
        const errorDetails = convertToErrorDetails(error, ErrorCode.QuoteUpgradeFailed);
        return getErrorDetails(errorDetails);
      });
  };

  const save = async (draft: Quote): Promise<QuoteModificationResponse> => {
    if (!draft.id) {
      return getErrorDetails({
        code: ErrorCode.QuoteSaveFailed,
        details: {
          additionalDetails: `Cannot save quote draft - draft id not provided.`,
        },
      });
    }

    const draftWithCustomer: Quote = {
      ...draft,
      customer: {
        firstName: session.user.firstName || draft.customer?.firstName,
        lastName: session.user.lastName || draft.customer?.lastName,
        gender: (session.user.gender as Gender | undefined) ?? draft.customer?.gender,
        dob: session.user.dateOfBirth || draft.customer?.dob,
        countryOfResidence: session.user.nationalityCountryCode || draft.customer?.countryOfResidence,
      },
    };

    return await fetchRetry(`${AppConfig.api.quotation}/quotes/${draft.id}`, {
      method: ApiRequestType.POST,
      headers: GenerateHeaderInformation(session.jwtAccessToken, JWT_API_KEY),
      body: JSON.stringify(draftWithCustomer),
    })
      .then((response) => returnJsonOrThrowError<Quote>(response))
      .then((result) => {
        if (!result?.id) {
          return getErrorDetails({
            code: ErrorCode.QuoteSaveFailed,
            details: {
              additionalDetails: `Saved quote was returned without an id.`,
            },
          });
        }
        result.createdAt = new Date().toISOString();
        addedQuote(result);
        return { quote: result as Quote, isSuccessful: true };
      })
      .catch((error) => {
        const errorDetails = convertToErrorDetails(error, ErrorCode.QuoteSaveFailed);
        return getErrorDetails(errorDetails);
      });
  };

  return (
    <QuoteDetailsContext.Provider
      value={{
        updateQuote: upgrade,
        saveQuote: save,
        fetchInstallments: fetchInstallments,
        payAllDiscount: payAllDiscount,
        quoteDraft: quoteDraft,
        loadingQuoteError: loadingError,
        loadingQuote: loadingQuoteDetails,
        reservationPrice: resPrice,
        loadingInstallments: loadingInstallments,
        installments: installments,
        paidFullDiscount: paidFullDiscount,
      }}
    >
      {children}
    </QuoteDetailsContext.Provider>
  );
};

const useQuoteDetailsContext = () => {
  return useContext(QuoteDetailsContext);
};

export { QuoteDetailsContextProvider, useQuoteDetailsContext };
